import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { TabContent, TabPane } from "reactstrap";
import { withRouter, useParams, useHistory } from "react-router-dom";

import {
  TABS,
  SPACE,
  ROUTES,
  ALL_EVENT,
  EMPTY_STRING,
  stepTitleKeys,
  ADD_RECAPZ_STEPS,
  localStorageKeys,
  eventBookingParams,
  eventBookingStatus,
  eventBookingConstants,
  numberConstants,
} from "../../../constants";
import { ReactComponent as CartIcon } from "../../../assets/icons/cart.svg";
import { ReactComponent as AddRecapIcon } from "../../../assets/icons/loop.svg";
import { ReactComponent as CheckoutIcon } from "../../../assets/icons/checkout.svg";
import { ReactComponent as EventSetupIcon } from "../../../assets/icons/event-setup.svg";
import { ReactComponent as EventDetailsIcon } from "../../../assets/icons/chart-detail.svg";
import { ReactComponent as ShutterbResultsIcon } from "../../../assets/icons/search-image.svg";

import {
  filterByStatus,
  normalizeEventDates,
  setStatusEventBooking,
  transformReelsInEditEvent,
  isMatchingDraftEventGroup,
  deepClone,
} from "../../../shared";
import {
  eventBookingActions,
  configurationsActions,
} from "../../../redux/actions";
import {
  NavigateToPath,
  setItemToStorage,
  getItemFromStorage,
  saveToLocalStorage,
} from "../../../system";
import AddRecapz from "./Tabs/AddRecapz";
import CartSummary from "./Tabs/CartSummary";
import EventDetails from "./Tabs/EventDetails";
import EventSetupForm from "./Tabs/EventSetupForm";
import ShutterBSelectionForm from "./Tabs/ShutterBSelectionForm";
import HorizontalStepper from "../../../shared/components/Stepper/HorizontalStepper";

const { TABS_ID, PARTNER_CODE, AFFILIATE_CODE } = eventBookingConstants;

const iconMap = {
  0: <EventSetupIcon />,
  1: <ShutterbResultsIcon />,
  2: <EventDetailsIcon />,
  3: <AddRecapIcon />,
  4: <CartIcon />,
  5: <CheckoutIcon />,
};

const tabClassMap = {
  [TABS_ID.CART]: "cart-summary-tab",
};

const EventBookingForm = (props) => {
  const history = useHistory();
  const { t } = useTranslation("common");
  const { isBookingStatus, eventGroupId, eventId, hasMultiEvents } =
    useParams();
  const isDraftEvent = isBookingStatus === eventBookingParams.DRAFT_EVENT;
  const isEditEvent =
    isBookingStatus === eventBookingParams.EDIT_EVENT ||
    isBookingStatus === eventBookingParams.ADD_UPDATE_RECAP ||
    isBookingStatus === eventBookingParams.UPDATE_RECAP;
  const isAddUpdateRecap =
    isBookingStatus === eventBookingParams.ADD_UPDATE_RECAP ||
    isBookingStatus === eventBookingParams.UPDATE_RECAP;
  const isUpdateRecap = isBookingStatus === eventBookingParams.UPDATE_RECAP;

  // Memoized values
  const affiliateCode = useMemo(() => localStorage.getItem(AFFILIATE_CODE), []);

  const partnerCodeUrlParam = useMemo(
    () => new URLSearchParams(history.location.search).get(PARTNER_CODE),
    [history.location.search]
  );

  const effectiveAffiliateCode = useMemo(
    () => partnerCodeUrlParam ?? affiliateCode,
    [partnerCodeUrlParam, affiliateCode]
  );

  const filteredFormState = useMemo(
    () => filterByStatus(props.formState ?? [], [eventBookingStatus.CANCEL]),
    [props.formState]
  );

  const editEventRef = useRef(true);
  const [avFormState, setAvFormState] = useState(0);
  const [stripeCancelUrl, setStripeCancelUrl] = useState(() => {
    return `/${isBookingStatus}/${eventGroupId}${eventId ? "/" + eventId : ""}`;
  });

  const [activeTab, setActiveTab] = useState(() => {
    const savedStep = getItemFromStorage(localStorageKeys.ACTIVE_TAB);
    const savedStepValue =
      savedStep !== null ? Number(savedStep) : TABS_ID.EVENT_SETUP;
    if (
      isMatchingDraftEventGroup(eventGroupId, props.draftEvents?.eventGroupId)
    ) {
      return savedStepValue;
    }

    if (isDraftEvent) {
      return TABS_ID.SELECT_SHUTTERB;
    } else if (isEditEvent && !isAddUpdateRecap && savedStep === null) {
      return TABS_ID.EVENT_SETUP;
    } else if (isAddUpdateRecap && savedStep === null) {
      return TABS_ID.ADD_REEL;
    }

    return savedStepValue;
  });

  useEffect(() => {
    if (eventGroupId && affiliateCode) {
      history.push(`?partnerCode=${affiliateCode}`);
    } else if (partnerCodeUrlParam) {
      localStorage.setItem("affiliateCode", partnerCodeUrlParam);

      if (affiliateCode !== partnerCodeUrlParam) {
        window.location.href = process.env.REACT_APP_MARKETING_SITE_BASE_URL;
      } else {
        history.push(
          `${process.env.REACT_APP_MARKETING_EVENT_BOOKING_URL}${partnerCodeUrlParam}`
        );
      }
    } else if (affiliateCode) {
      // Use history.push() for redirection
      history.push(
        `${process.env.REACT_APP_MARKETING_EVENT_BOOKING_URL}${affiliateCode}`
      );
    }
    // eslint-disable-next-line
  }, [history.location.search]);

  useEffect(() => {
    props.getRecapTypes();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const savedStep = getItemFromStorage(localStorageKeys.ACTIVE_TAB);
    setItemToStorage(localStorageKeys.ACTIVE_TAB, activeTab);
    props.setPreviousActiveTab(t(TABS[activeTab - 1]) || EMPTY_STRING);
    if (activeTab === TABS_ID.EVENT_SETUP || activeTab === TABS_ID.ADD_REEL) {
      props.getPostalCodes();

      if (savedStep !== null) {
        if (props.editEvents?.stripeOrderPayment) {
          // mutiply by 100 because later use method (calculatePercentage) in cart summary table page
          // which required the actual value of percentage
          props.setMiscAmountsOld({
            tax: props.editEvents.taxPercentage * numberConstants.ONE_HUNDRED,
            serviceFee:
              props.editEvents.serviceFeePercentage *
              numberConstants.ONE_HUNDRED,
          });
        }
        return;
      }

      if (isEditEvent && editEventRef.current) {
        props.resetEventBooking().then(() => {
          props.getEditEvents(
            eventGroupId,
            eventId,
            isAddUpdateRecap,
            onGetEditEventSuccess
          );
          editEventRef.current = false;
        });
        editEventRef.current = false;
      }
    }

    // eslint-disable-next-line
  }, [activeTab]);

  // This function is triggered when the "get edit events" call is executed,
  // and it handles either a response or null.
  const onGetEditEventSuccess = async (response) => {
    if (response) {
      let updatedFormState = normalizeEventDates(response.events);

      // Set status "EDITED" for events
      // Set status "EDITED" and oldHourlyRate for orderItems inside each event
      updatedFormState = setStatusEventBooking(
        updatedFormState.map((event) => ({
          ...event,
          orderItems: setStatusEventBooking(
            event?.orderItems?.map((item) => ({
              ...item,
              hourlyRate: item?.sbUser?.hourlyRate ?? numberConstants.ZERO,
              oldHourlyRate:
                item?.sbUser?.oldHourlyRate ?? numberConstants.ZERO,
            })),
            eventBookingStatus.EDITED
          ),
          oldDuration: event.duration ?? numberConstants.ZERO,
        })),
        eventBookingStatus.EDITED
      );

      props.setFormState(updatedFormState);
      props.setEditEvents({ ...response, events: updatedFormState });
      props.setIsEditEvent(isEditEvent);

      // Update the structure of recaps to show in the Add Recap page in event booking flow
      const updatedRecapz = transformReelsInEditEvent(
        response.reels,
        ALL_EVENT.id
      );

      const backupRecapz = deepClone(updatedRecapz);

      props.setAddRecapz(updatedRecapz);
      // backup recaps in case of Edit Event to restore the cancel recaps while editing event
      props.setAddRecapzBackup(backupRecapz);
      props.setTotalEvents(response.totalOrderEvents);
      props.setBillingDetails(response?.billingDetails ?? null);

      if (updatedRecapz?.length > numberConstants.ZERO) {
        saveToLocalStorage(
          localStorageKeys.ADD_RECAPZ_ACTIVE_STEP,
          ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY
        );
      }

      if (response.stripeOrderPayment) {
        // mutiply by 100 because later use method (calculatePercentage) in cart summary table page
        // which required the actual value of percentage
        props.setMiscAmountsOld({
          tax: response.taxPercentage * numberConstants.ONE_HUNDRED,
          serviceFee:
            response.serviceFeePercentage * numberConstants.ONE_HUNDRED,
        });
      }
    } else {
      // If the response is null then update the path to "book new event"
      NavigateToPath(history, ROUTES.EVENT_BOOKING);
    }
  };

  const handleNext = useCallback(() => {
    setActiveTab((prev) => prev + numberConstants.ONE);
  }, []);

  const handlePrevious = useCallback(() => {
    setActiveTab((prev) => prev - numberConstants.ONE);
  }, []);

  const handleStepClick = useCallback(
    (index) => {
      if (index <= activeTab) {
        setActiveTab(index);
        props.setIsProfileViewShow(false);
      }
    },
    [activeTab]
  );

  const resetAvFormKey = useCallback(() => {
    setAvFormState((prev) => prev + numberConstants.ONE);
  }, []);

  // Render tab
  const renderTab = () => {
    switch (activeTab) {
      case TABS_ID.EVENT_SETUP:
        return (
          <EventSetupForm
            onNext={handleNext}
            avFormState={avFormState}
            hasMultiEvents={hasMultiEvents}
          />
        );

      case TABS_ID.SELECT_SHUTTERB:
        return (
          <ShutterBSelectionForm
            onNext={handleNext}
            onPrevious={handlePrevious}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            resetAvFormKey={resetAvFormKey}
            affiliateCode={effectiveAffiliateCode}
            formState={filteredFormState}
          />
        );

      case TABS_ID.EVENT_DETAIL:
        return (
          <EventDetails
            onNext={handleNext}
            onPrevious={handlePrevious}
            activeTab={activeTab}
          />
        );

      case TABS_ID.ADD_REEL:
        return (
          <AddRecapz
            onNext={handleNext}
            onPrevious={handlePrevious}
            activeTab={activeTab}
            isAddUpdateRecap={isAddUpdateRecap}
            isUpdateRecap={isUpdateRecap}
          />
        );

      case TABS_ID.CART:
        return (
          <CartSummary
            onNext={handleNext}
            onPrevious={handlePrevious}
            activeTab={activeTab}
            affiliateCode={effectiveAffiliateCode}
            isAddUpdateRecap={isAddUpdateRecap}
            stripeCancelUrl={stripeCancelUrl}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="event-booking-container position-relative my-5">
      <div className="heading-wrapper text-center mb-5">
        <h2 className="heading-title mb-2">
          {activeTab === TABS_ID.EVENT_DETAIL
            ? t(stepTitleKeys[activeTab])
            : activeTab === TABS_ID.EVENT_SETUP ||
                activeTab === TABS_ID.SELECT_SHUTTERB
              ? t(stepTitleKeys[activeTab])
              : t(stepTitleKeys[activeTab]) +
                SPACE +
                (props.eventName || EMPTY_STRING)}
        </h2>
        <hr className="heading-hr" />
      </div>

      <HorizontalStepper
        activeStep={activeTab}
        steps={TABS}
        onStepClick={handleStepClick}
        iconsMap={iconMap}
        disableInActiveTabs={!props.isSBTipExtendCart}
        isDraftEvent={props.isDraftEvent}
        isAddUpdateRecap={isAddUpdateRecap}
        TABS_ID={TABS_ID}
      />

      <TabContent activeTab={activeTab} className="tabs_content">
        <TabPane
          tabId={activeTab}
          className={tabClassMap[activeTab] || EMPTY_STRING}
        >
          {renderTab()}
        </TabPane>
      </TabContent>
    </div>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  eventName: state.eventBooking.eventName,
  formState: state.eventBooking.formState,
  editEvents: state.eventBooking.editEvents,
  draftEvents: state.eventBooking.draftEvents,
  isDraftEvent: state.eventBooking.isDraftEvent,
  isSBTipExtendCart: state.eventBooking.isSBTipExtendCart,
  isProfileViewShow: state.eventBooking.isProfileViewShow,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getRecapTypes: () => dispatch(eventBookingActions.getRecapTypes()),
  getPostalCodes: () => dispatch(configurationsActions.getPostalCodes()),
  setFormState: (data) => dispatch(eventBookingActions.setFormState(data)),
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setEditEvents: (data) => dispatch(eventBookingActions.setEditEvents(data)),
  setIsEditEvent: (data) => dispatch(eventBookingActions.setIsEditEvent(data)),
  setTotalEvents: (data) => dispatch(eventBookingActions.setTotalEvents(data)),
  setMiscAmountsOld: (data) =>
    dispatch(eventBookingActions.setMiscAmountsOld(data)),
  setAddRecapzBackup: (data) =>
    dispatch(eventBookingActions.setAddRecapzBackup(data)),
  setIsProfileViewShow: (data) =>
    dispatch(eventBookingActions.setIsProfileViewShow(data)),
  setPreviousActiveTab: (data) =>
    dispatch(eventBookingActions.setPreviousActiveTab(data)),
  setBillingDetails: (data) =>
    dispatch(eventBookingActions.setBillingDetails(data)),
  getEditEvents: (
    eventGroupId,
    eventId,
    isAddUpdateRecap,
    onResponseCallback
  ) =>
    dispatch(
      eventBookingActions.getEditEvents(
        eventGroupId,
        eventId,
        isAddUpdateRecap,
        onResponseCallback
      )
    ),
  resetEventBooking: () => dispatch(eventBookingActions.resetEventBooking()),
});

// mapping action and store the function via props
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(EventBookingForm));
